import {
  ReactNode,
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';

import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { AudioItem, SpeechMark } from './tts.constants';
import { ttsGetTextBlockSelector } from './logic/tts-get-text-block-selector';
import {
  ttsInjectSpeakableInPlace,
  ttsRemoveSpeakableInPlace,
} from './logic/tts-inject-speakable-in-place';
import {
  ttsClearHighlightText,
  ttsHighlightText,
} from './logic/tts-highlight-text';
import { adjustPausedSpeechMarks } from './logic/tts-adjust-paused-speech-marks';
import { resetSpeechMarks } from './logic/tts-reset-speech-marks';
import { logException } from 'approot/shared/debug';

type SpeakableSpeechMark = SpeechMark & {
  played?: boolean;
};

type UnParsedText = {
  id: string;
  innerHTML: string | null | undefined;
};

type TTSContextType = {
  audioList: AudioItem[];
  currentAudio: AudioItem | null;
  isPlaying: boolean;
  isLoading: boolean;
  isPaused: boolean;
  parsedText: string | null;
  playAudio: (id: string) => void;
  pauseAudio: () => void;
  addAudio: (audio: AudioItem) => void;
  getAudio: (id: string) => AudioItem | undefined;
  restartAudio: (id: string) => void;
  stopAudio: (id: string) => void;
  tearDown: () => void;
};

const noop = () => {};

export const TTSContext = createContext<TTSContextType>({
  audioList: [],
  currentAudio: null,
  isPlaying: false,
  isLoading: false,
  isPaused: false,
  parsedText: null,
  playAudio: noop,
  pauseAudio: noop,
  addAudio: noop,
  getAudio: () => undefined,
  restartAudio: noop,
  stopAudio: noop,
  tearDown: noop,
});

export const TTSProvider = ({ children }: { children: ReactNode }) => {
  const audioCache = useRef<Map<string, HTMLAudioElement>>(new Map());
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const currentAudioIdRef = useRef<string>('');
  const audioListRef = useRef<AudioItem[]>([]);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const inPlaceContainerRef = useRef<HTMLElement | null>(null);

  const [currentAudio, setCurrentAudio] = useState<AudioItem | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // highlighting
  const [unParsedText, setUnParsedText] = useState<UnParsedText | null>(null);
  const [parsedText, setParsedText] = useState<string | null>(null);
  const speechMarkWordsRef = useRef<SpeakableSpeechMark[]>([]);

  const { showError } = useSnackbar();

  const setPlayTimings = useCallback(() => {
    speechMarkWordsRef.current?.forEach((mark, index) => {
      if (!mark.played) {
        const timeout = setTimeout(() => {
          ttsHighlightText(
            mark.time,
            speechMarkWordsRef.current[index - 1]?.time
          );
          mark.played = true;
        }, mark.time);
        timeoutRefs.current.push(timeout);
      }
    });
  }, []);

  const addAudio = useCallback((audio: AudioItem) => {
    const list = [...audioListRef.current, audio];
    audioListRef.current = list;
  }, []);

  const getAudio = useCallback((itemId: string) => {
    return (
      audioListRef.current.find(
        item => item.itemId === itemId && item.audioUrl
      ) || undefined
    );
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsPaused(false);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(true);
  }, []);

  const handleCanPlayThrough = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    showError('There was a problem playing the audio. Please try again later.');
  }, [showError]);

  const clearInPlaceAndHighlight = useCallback((id: string) => {
    ttsClearHighlightText(id);
    if (inPlaceContainerRef.current) {
      ttsRemoveSpeakableInPlace(inPlaceContainerRef.current);
      inPlaceContainerRef.current = null;
    }
  }, []);

  const handleEnded = useCallback(
    (e: Event) => {
      if (e.target instanceof HTMLAudioElement) {
        e.target.currentTime = 0;
      }
      setParsedText(null);
      clearInPlaceAndHighlight(currentAudioIdRef.current);
      setIsPlaying(false);
      setIsPaused(false);
    },
    [clearInPlaceAndHighlight]
  );

  const cleanUpAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.removeEventListener('error', handleError);
      currentAudioRef.current.removeEventListener('play', handlePlay);
      currentAudioRef.current.removeEventListener('pause', handlePause);
      currentAudioRef.current.removeEventListener('ended', handleEnded);
      currentAudioRef.current.removeEventListener(
        'canplaythrough',
        handleCanPlayThrough
      );
      currentAudioRef.current = null;
    }
    timeoutRefs.current.forEach(clearTimeout);
    setParsedText(null);
    clearInPlaceAndHighlight(currentAudioIdRef.current);
  }, [
    handleError,
    handlePlay,
    handlePause,
    handleEnded,
    handleCanPlayThrough,
    clearInPlaceAndHighlight,
  ]);

  const playAudio = useCallback(
    async (id: string) => {
      const audioItem = getAudio(id);
      if (!audioItem) return;

      cleanUpAudio();

      let audio = audioCache.current.get(id);
      if (!audio && audioItem.audioUrl) {
        // Cache unplayed audio
        audio = new Audio(audioItem.audioUrl);
        audioCache.current.set(id, audio);
      }

      let text = unParsedText;
      if (!unParsedText || unParsedText.id !== id) {
        const textBlock = document.getElementById(ttsGetTextBlockSelector(id));
        text = {
          id,
          innerHTML: textBlock?.innerHTML,
        };
        setUnParsedText(text);
      }

      if (audio) {
        if (audio.currentTime > 0 && speechMarkWordsRef.current.length > 0) {
          // audio was paused
          speechMarkWordsRef.current = adjustPausedSpeechMarks({
            speakableSpeechMarks: speechMarkWordsRef.current,
            lastPlayedMarkTime: audio.currentTime * 1000,
            sourceSpeechMarks: audioItem.speechMarks,
          });
        } else {
          speechMarkWordsRef.current = resetSpeechMarks(audioItem.speechMarks);
        }

        currentAudioIdRef.current = id;

        const container = document.getElementById(ttsGetTextBlockSelector(id));
        if (container && speechMarkWordsRef.current.length > 0) {
          inPlaceContainerRef.current = container;
          ttsInjectSpeakableInPlace(container, speechMarkWordsRef.current);
        }

        setCurrentAudio(() => audioItem);

        audio.addEventListener('error', handleError, { once: true });
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('canplaythrough', handleCanPlayThrough, {
          once: true,
        });

        currentAudioRef.current = audio;

        audio
          .play()
          .then(() => {
            setPlayTimings();
          })
          .catch(e => {
            handleError();
            logException({
              sourceFile: 'tts.context.tsx',
              message: `Error playing audio for component: ${id}. Message: ${e} Content: ${window.location.href}`,
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [
      unParsedText,
      cleanUpAudio,
      getAudio,
      handleError,
      handlePlay,
      handlePause,
      handleEnded,
      handleCanPlayThrough,
      setPlayTimings,
    ]
  );

  const pauseAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      setIsPaused(true);

      // clear highlight timeouts
      timeoutRefs.current.forEach(clearTimeout);
    }
  }, []);

  const stopAudio = useCallback(
    (id: string) => {
      timeoutRefs.current.forEach(clearTimeout);

      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      }
      setParsedText(null);
      clearInPlaceAndHighlight(id);
    },
    [clearInPlaceAndHighlight]
  );

  const restartAudio = useCallback(
    (id: string) => {
      stopAudio(id);
      setParsedText(null);

      setTimeout(() => {
        const audioItem = getAudio(id);
        if (!audioItem) return;

        speechMarkWordsRef.current = resetSpeechMarks(audioItem.speechMarks);

        const container = document.getElementById(ttsGetTextBlockSelector(id));
        if (container && speechMarkWordsRef.current.length > 0) {
          inPlaceContainerRef.current = container;
          ttsInjectSpeakableInPlace(container, speechMarkWordsRef.current);
        }

        if (currentAudioRef.current) {
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current
            .play()
            .then(() => {
              setPlayTimings();
            })
            .catch(e => {
              handleError();
              logException({
                sourceFile: 'tts.context.tsx',
                message: `Error playing audio for component: ${id}. Message: ${e} Content: ${window.location.href}`,
              });
            });
        }
      }, 100);
    },
    [setPlayTimings, stopAudio, getAudio, handleError]
  );

  const tearDown = useCallback(() => {
    const id = currentAudio?.itemId || currentAudioIdRef.current || '';
    stopAudio(id);
    cleanUpAudio();

    // reset cached audio state
    let audio = audioCache.current.get(id);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    speechMarkWordsRef.current = [];

    setUnParsedText(null);
    setParsedText(null);
  }, [cleanUpAudio, stopAudio, currentAudio]);

  useEffect(() => {
    return () => tearDown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      audioList: audioListRef.current,
      currentAudio,
      isPlaying,
      isLoading,
      isPaused,
      parsedText,
      playAudio,
      pauseAudio,
      addAudio,
      getAudio,
      restartAudio,
      stopAudio,
      tearDown,
    }),
    [
      currentAudio,
      isPlaying,
      isLoading,
      isPaused,
      parsedText,
      playAudio,
      pauseAudio,
      addAudio,
      getAudio,
      restartAudio,
      stopAudio,
      tearDown,
    ]
  );

  return <TTSContext.Provider value={value}>{children}</TTSContext.Provider>;
};

export const useTTSContext = ({ id }: { id?: string }) => {
  const ctx = useContext(TTSContext);

  return {
    ...ctx,
    // only return parsed text for the current id using audio
    parsedText: ctx.currentAudio?.itemId === id ? ctx.parsedText : null,
  };
};
